<template>
    <div class="ts-border ts-border-gray-300 ts-rounded-lg ts-p-3">
        <form class="form-horizontal">
            <div class="row">
                <div class="col-md-6 ts-space-y-3">
                    <div class="row">
                        <label class="col-md-3 col-form-label required">
                            {{ $t('serviceTicket.serviceDate') }}
                        </label>
                        <div class="col-md-7">
                            <a-date-picker v-model="model.service_date" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
                                class="ts-w-full" />
                            <div class="validate" v-if="errors.has('service_date')">
                                {{ errors.first('service_date') }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <label class="col-md-3 col-form-label" :class="{ 'required': model.ticket_status_id == 2 }">
                            {{ $t('serviceTicket.ticketOwner') }}
                        </label>
                        <div class="col-md-7">
                            <a-select v-model="model.ticket_owner" :options="employees" showSearch :filterOption="(input, option) =>
                                option.componentOptions.children[0].text
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                " @search="handleSearchEmployee" @change="onChangeTicketOwner" class="ts-w-full"
                                :class="{ 'is-invalid': errors.has('ticket_owner') }"></a-select>
                            <div class="text-danger" v-if="errors.has('ticket_owner')">
                                {{ errors.first('ticket_owner') }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <label class="col-md-3 col-form-label required">
                            {{ $t('serviceTicket.ticketStatus') }}
                        </label>
                        <div class="col-md-7">
                            <a-select v-model="model.ticket_status_id" :options="serviceTicketStatus"
                                @change="onChangeTicketStatus" class="ts-w-full"></a-select>
                            <div class="text-danger" v-if="errors.has('ticket_status')">
                                {{ errors.first('ticket_status') }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-6 ts-space-y-3">
                    <div class="row">
                        <label class="col-md-3 col-form-label required">
                            {{ $t('serviceTicket.customer') }}
                        </label>
                        <div class="col-md-7">
                            <div class="ts-flex customer">
                                <a-select v-model="model.customer_id" :options="customers" showSearch :filterOption="(input, option) =>
                                    option.componentOptions.children[0].text
                                        .toLowerCase()
                                        .indexOf(input.toLowerCase()) >= 0
                                    " class="ts-w-full" :class="{ 'is-invalid': errors.has('customer_id') }"
                                    @search="handleSearchCustomer"></a-select>
                                <a href="#" @click.prevent="onCreateCustomer" v-tooltip="$t('addNew')"
                                    style="background: #3990e0"
                                    class="ts-h-8 ts-w-8 ts-text-white ts-flex ts-items-center ts-rounded-tr ts-rounded-br hover:ts-text-white"><i
                                        class="fas fa-plus-square ts-mx-auto"></i></a>
                            </div>
                            <div class="text-danger" v-if="errors.has('customer_id')">
                                {{ errors.first('customer_id') }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <label class="col-md-3 col-form-label required">
                            {{ $t('serviceTicket.priority') }}
                        </label>
                        <div class="col-md-7">
                            <a-select v-model="model.priority" :options="priorities" class="ts-w-full"></a-select>
                            <div class="text-danger" v-if="errors.has('priorities')">
                                {{ errors.first('priorities') }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <label class="col-md-3 col-form-label">
                            {{ $t('serviceTicket.remarks') }}
                        </label>
                        <div class="col-md-7">
                            <textarea v-model="model.remarks" rows="3" class="form-control" :class="{
                                'is-invalid': errors.has('remarks')
                            }" />
                            <div class="validate" v-if="errors.has('remarks')">
                                {{ errors.first('remarks') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Drawer :title="$t('serviceTicket.createNewCustomer')" :closable="false" :value="add_new_customer"
                @on-close="() => (add_new_customer = false)" width="400px">
                <AddNewCustomer ref="add_new_customer" @cancel="() => add_new_customer = false" @data="setCustomer" />
            </Drawer>
        </form>
    </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import AddNewCustomer from './add-new-customer.vue'
import { debounce } from 'lodash'

export default {
    name: 'Info',
    props: ['value', 'validate'],
    components: {
        AddNewCustomer
    },
    data() {
        return {
            errors: new Errors(),
            add_new_customer: false,
            waiting_new: false,
            waiting: false,
            loading: false,
            model: {},
            is_disabled: false,
            searching: false
        }
    },
    created() {
        this.model = this.value
    },
    computed: {
        ...mapState('customerService/serviceTicket', ['formViewDatas']),
        customers() {
            if (this.formViewDatas.customers) {
                return this.formViewDatas.customers
                    .map(el => ({
                        value: el.customer_id,
                        label: el.customer_name
                    }))
            }
            return []
        },
        employees() {
            if (this.formViewDatas.employees) {
                return this.formViewDatas.employees
                    .map(el => ({
                        value: el.employee_id,
                        label: el.employee_name_en
                    }))
            }
            return []
        },
        serviceTicketStatus() {
            if (this.formViewDatas.serviceTicketStatus) {
                return this.formViewDatas.serviceTicketStatus
                    .filter(el => [1, 2].includes(el.ticket_status_id))
                    .map(el => ({
                        value: el.ticket_status_id,
                        label: el.ticket_status
                    }))
            }
            return []
        },
        priorities() {
            return [
                {
                    value: 'low',
                    label: 'Low'
                },
                {
                    value: 'normal',
                    label: 'Normal'
                },
                {
                    value: 'urgent',
                    label: 'Urgent'
                }
            ]
        }
    },
    methods: {
        ...mapActions('customerService/serviceTicket', ['getFormViewModel']),
        handleSearchEmployee: debounce(function (value) {
            this.loading = true
            this.getFormViewModel({
                index: 'employees',
                params: {
                    fnName: 'employees',
                    search: value
                }
            }).finally(() => (this.loading = false))
        }, 800),
        handleSearchCustomer: debounce(function (value) {
            this.loading = true
            this.getFormViewModel({
                index: 'customers',
                params: {
                    fnName: 'customers',
                    search: value
                }
            }).finally(() => (this.loading = false))
        }, 800),
        getTicketStatusList() {
            if (this.model.ticket_status_id && this.ticketStatusList) {
                if (this.model.ticket_status_id == 1 || this.model.ticket_status_id == 2) {
                    return this.serviceTicketStatus
                        .filter(el => includes([1, 2], el.value))
                } else if (this.model.ticket_status_id == 3 || this.model.ticket_status_id == 4 || this.model.ticket_status_id == 5) {
                    return this.serviceTicketStatus
                        .filter(el => includes([3, 4, 5], el.value))
                }
                else if (this.model.ticket_status_id == 6 || this.model.ticket_status_id == 7) {
                    return this.serviceTicketStatus
                        .filter(el => includes([6, 7], el.value))
                }
            } else {
                return this.serviceTicketStatus
            }
        },
        onChangeTicketOwner(value) {
            if (value) {
                this.model.ticket_status_id = 2
            } else {
                this.model.ticket_status_id = 1
            }
        },
        onChangeTicketStatus(value) {
            if (value == 1) {
                this.model.ticket_owner = null
            }
        },
        setCustomer(data) {
            let exist = this.formViewDatas.customers.find(c => c.customer_id == data.customer_id)
            if (!exist) {
                this.formViewDatas.customers.push({
                    contact_number: data.contact_number,
                    customer_id: data.customer_id,
                    customer_name: data.customer_name,
                    front_photo: data.front_photo
                })
            }
            this.model.customer_id = data.customer_id
        },
        onCreateCustomer() {
            this.$refs.add_new_customer.fetchResources()
            this.add_new_customer = true
        },
    },
    watch: {
        validate: function (error) {
            this.errors = new Errors(error.errors)
        }
    }
}
</script>
<style>
.customer .ant-select-selection {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.ant-input {
    height: 33px !important;
}
</style>